/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import MDCheckbox from 'components/MDCheckbox';
import MDSelect from 'components/MDSelect';
import MDSelectAuto from 'components/MDSelectAuto';
import TableHeader from 'components/Table/TableHeader';
import TabPanel from 'components/TabPanel/TabPanel';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { setToken, setDevice, setUser, setLang, setPerm, setMenu } from 'redux/actions/appAction';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import nopic from 'assets/images/nopic.jpg';
import UserUpload from './UserUpload';
import UploadSignature from './UploadSignature';
import SearchCustomer from './SearchCustomer';
import UserClient from './UserClient';

const initAttr = {
  id: '',
  code: '',
  role_id: '',
  image_id: '',
  branch_id: '',
  prefix_id: '',
  position_id: '',
  department_id: '',
  office_id: '',
  office_name: '',
  name: '',
  surname: '',
  tel: '',
  email: '',
  user_name: '',
  idcard: '',
  user_pass: '',
  user_pass_again: '',
  address: '',
  province_id: '',
  district_id: '',
  subdistrict_id: '',
  line_code: '',
  zipcode: '',
  line_pic: '',
  line_name: '',
  line_status: '',
  line_email: '',
  image: nopic,
  signature: nopic,
  enable: 'Y',
  login_appstock: 'N',
  login_apppos: 'N',
  driver: 'N',
  branch_all: 'N',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

function User() {
  const mod = 'user';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [cboProvince, setCboProvince] = useState([]);
  const [cboDistrict, setCboDistrict] = useState([]);
  const [cboSubDistrict, setCboSubDistrict] = useState([]);
  const [cboPrefix, setCboPrefix] = useState([]);
  const [cboPosition, setCboPosition] = useState([]);
  const [cboDepartment, setCboDepartment] = useState([]);
  const [cboRole, setCboRole] = useState([]);
  const [cboBranch, setCboBranch] = useState([]);
  const [branch, setBranch] = useState([]);
  const [tab, setTab] = useState(0);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.image_id', label: t(`attr.image`), align: 'center' },
    { id: 'tb.id', label: t(`attr.id`), align: 'center' },
    { id: 'tb.code', label: t(`attr.code`), align: 'center' },
    { id: 'tb.name', label: t(`attr.firstname`), align: 'left' },
    { id: 'tb.surname', label: t(`attr.lastname`), align: 'left' },
    { id: 'tb.role_id', label: 'สิทธิ์การใช้', align: 'left' },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
    { id: 'tb.user_name', label: 'Username', align: 'left' },
    { id: 'tb.login_appstock', label: 'Stock', align: 'center' },
    { id: 'tb.login_apppos', label: 'POS', align: 'center' },
    { id: 'tb.driver', label: 'คนขับ', align: 'center' },
    { id: 'tb.enable', label: t(`attr.enable`), align: 'center' },
    { id: 'message', label: '', align: 'center', sort: false, width: 40 },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const checkBrowser = () => {
    const ua = window.navigator.userAgent;
    let tem;
    let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
    if (/trident/i.test(M[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
      return 'IE '.concat(tem[1] || '');
    }
    if (M[1] === 'Chrome') {
      tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
      if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
    }
    M = M[2] ? [M[1], M[2]] : [window.navigator.appName, window.navigator.appVersion, '-?'];
    tem = ua.match(/version\/(\d+)/i);
    if (tem != null) M.splice(1, 1, tem[1]);
    return M.join(' ');
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      role_id: data.role_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const message = (item) => {
    Swal.fire({
      title: `กรุณาระบุข้อความ`,
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'ข้อความ',
      },
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'ส่งข้อความ',
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        const msg = Swal.getInput()?.value || '';
        if (msg === '') {
          showNotify('กรุณากรอกข้อความ', 'error');
          return;
        }

        setLoading(true);
        FetchData('OPTIONS', `/${mod}/message`, app.token, {
          id: item.id,
          msg: msg,
        }).then((result) => {
          setLoading(false);
          if (result.status === 'ok') {
            showNotify('ส่งข้อความเรียบร้อยแล้ว', 'success');
          } else {
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const district = (province_id) => {
    setCboDistrict([]);
    setCboSubDistrict([]);
    FetchData('OPTIONS', `/${mod}/district`, app.token, {
      lang: app.lang,
      province_id: province_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboDistrict(result.district);
      } else {
        Func.exitApp();
      }
    });
  };

  const subDistrict = (district_id) => {
    setCboSubDistrict([]);
    FetchData('OPTIONS', `/${mod}/subdistrict`, app.token, {
      lang: app.lang,
      district_id: district_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboSubDistrict(result.subdistrict);
      } else {
        Func.exitApp();
      }
    });
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setBranch([]);
    setTab(0);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setNavigate(result.navigate);
        district(result.data.province_id, result.data.district_id);
        subDistrict(result.data.district_id);
        setBranch(result.branch);
        setAttr({
          ...result.data,
          user_pass: '',
          user_pass_again: '',
          tel: Func.maskTel(result.data.tel),
        });

        setLoading(false);
      }
    });
  };

  const selectCustomer = (item) => {
    setAttr({
      ...attr,
      customer_id: item.id,
      line_code: item.line_userid,
      line_name: item.line_name,
      line_status: item.line_status,
      line_pic: item.image,
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.code, 3)) return;
    if (!Func.verifyLength(attr.name, 1)) return;
    if (!Func.verifyLength(attr.branch_id, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      code: attr.code,
      name: attr.name,
      surname: attr.surname,
      branch_id: attr.branch_id,
      prefix_id: attr.prefix_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.code, 3)) return false;
    if (!Func.verifyLength(attr.name, 1)) return false;
    if (!Func.verifyLength(attr.branch_id, 1)) return false;

    return true;
  };

  const edit = () => {
    if (!editConfirm()) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          code: attr.code,
          prefix_id: attr.prefix_id,
          position_id: attr.position_id,
          department_id: attr.department_id,
          name: attr.name,
          surname: attr.surname,
          idcard: attr.idcard,
          role_id: attr.role_id,
          tel: Func.formatTel(attr.tel),
          branch_id: attr.branch_id,
          address: attr.address,
          email: attr.email,
          province_id: attr.province_id,
          subdistrict_id: attr.subdistrict_id,
          district_id: attr.district_id,
          zipcode: attr.zipcode,
          enable: attr.enable,
          login_appstock: attr.login_appstock,
          login_apppos: attr.login_apppos,
          driver: attr.driver,
          line_code: attr.line_code,
          customer_id: attr.customer_id,
          branch_all: attr.branch_all,
          branch: branch,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            open();

            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const registerConfirm = () => {
    if (!Func.verifyLength(attr.user_name, 5)) return false;
    if (!Func.verifyLength(attr.user_pass, 5)) return false;
    if (attr.user_pass !== attr.user_pass_again) return false;

    return true;
  };

  const register = () => {
    if (!registerConfirm()) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/register`, app.token, {
          id: id,
          username: attr.user_name,
          password: attr.user_pass,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            setAttr({
              ...attr,
              user_pass: '',
              user_pass_again: '',
            });

            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const del = () => {
    setLoading(true);
    setModalDel(false);
    FetchData('DELETE', `/${mod}/${attr.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const upload = (filedata) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        FetchData('OPTIONS', `/${mod}/upload`, app.token, {
          id: id,
          filedata: filedata,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            setAttr({
              ...attr,
              image: result.url,
            });

            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const signature = (filedata, file) => {
    const fileSize = file ? file.size : 0;
    if (fileSize > 1000000) {
      showNotify('ขนาดไฟล์ใหญ่จนเกินไป', 'error');
      return;
    }

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/signature`, app.token, {
          id: id,
          filedata: filedata,
          filename: file ? file.name : '',
          filesize: file ? file.size : 0,
          filetype: file ? file.type : '',
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            setAttr({
              ...attr,
              signature: result.url,
            });
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const signIn = () => {
    Swal.fire({
      title: 'คุณต้องการเข้าสู่ระบบใช่หรือไม่',
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        FetchData('OPTIONS', `/${mod}/signin`, app.token, {
          lang: app.lang,
          client: app.client,
          browser: checkBrowser(),
          platform: window.navigator.platform,
          user_id: attr.id,
        }).then((result) => {
          if (result.status === 'ok') {
            dispatch(setToken(result.token));
            dispatch(setUser(result.user));
            dispatch(setMenu(result.menu));
            dispatch(setPerm(result.perm));
            dispatch(setLang(result.user.language));
            dispatch(setDevice(result.user.device));

            setTimeout(function () {
              window.location.replace(result.url);
            }, 500);
          } else {
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const btnMessage = (item) => {
    if (item.line_code === '') return null;

    return (
      <Tooltip title='ส่งข้อความ'>
        <MDButton variant='text' color='dark' iconOnly onClick={() => message(item)}>
          <Icon>email</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboProvince(result.province);
        setCboRole(result.role);
        setCboBranch(result.branch);
        setCboPosition(result.position);
        setCboPrefix(result.prefix);
        setCboDepartment(result.department);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setCboDistrict([]);
                setCboSubDistrict([]);
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center' width={40}>
                        {Func.imageDisplay(row)}
                      </TableCell>
                      <TableCell align='left'>{row.id}</TableCell>
                      <TableCell align='left'>{btnOpen(row)}</TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.name}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.surname}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.role_name}
                      </TableCell>
                      <TableCell align='left'>{row.branch_name}</TableCell>
                      <TableCell align='left'>{row.user_name}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.login_appstock)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.login_apppos)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.driver)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.enable)}</TableCell>
                      <TableCell align='center'>{btnMessage(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderImage = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          Avatar
        </MDTypography>
        <MDBox>
          {attr.image === '' ? null : (
            <MDBox component='img' src={attr.image} alt={attr.name} width='100%' height='100%' />
          )}
          <MDBox mr={1} display='flex' justifyContent='center'>
            <UserUpload uploadData={upload} />
          </MDBox>
        </MDBox>
      </Paper>
    );
  };

  const renderSignature = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          ลายเซ็น (500 x 150 Pixels)
        </MDTypography>
        <MDBox>
          {attr.signature === '' ? null : (
            <MDBox
              component='img'
              src={attr.signature}
              alt={attr.name}
              width='100%'
              height='100%'
            />
          )}
          <MDBox mr={1} display='flex' justifyContent='center'>
            <UploadSignature uploadData={signature} />
          </MDBox>
        </MDBox>
      </Paper>
    );
  };

  const renderAuthen = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          Authentication
        </MDTypography>
        <MDBox>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDInput
                label='Username'
                onChange={(e) => setAttr({ ...attr, user_name: e.target.value })}
                value={attr.user_name}
                variant='standard'
                inputProps={{
                  autoComplete: 'off',
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='รหัสผ่าน'
                onChange={(e) => setAttr({ ...attr, user_pass: e.target.value })}
                value={attr.user_pass}
                variant='standard'
                type='password'
                inputProps={{
                  autoComplete: 'off',
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='รหัสผ่าน อีกครั้ง'
                onChange={(e) => setAttr({ ...attr, user_pass_again: e.target.value })}
                value={attr.user_pass_again}
                variant='standard'
                type='password'
                inputProps={{
                  autoComplete: 'off',
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </MDBox>

        <Grid container mt={3}>
          <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
            <MDButton variant='outlined' color='success' onClick={register}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='รหัสพนักงาน *'
            onChange={(e) => setAttr({ ...attr, code: e.target.value })}
            value={attr.code}
            variant='standard'
            inputProps={{
              maxLength: 10,
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label='คำนำหน้า *'
            labelDefault={t(`element.please_select`)}
            data={cboPrefix}
            value={attr.prefix_id}
            onChange={(e) => setAttr({ ...attr, prefix_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ชื่อ *'
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='นามสกุล'
            onChange={(e) => setAttr({ ...attr, surname: e.target.value })}
            value={attr.surname}
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label='สาขา *'
            labelDefault={t(`element.please_select`)}
            data={cboBranch}
            value={attr.branch_id}
            onChange={(e) => setAttr({ ...attr, branch_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='xs' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='xs' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant='text' color='success' onClick={edit}>
            <Icon>save</Icon>&nbsp;{t(`element.save`)}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-delete'
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalDel(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={5}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='gradient' color='error' onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='error' onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderFormInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          ข้อมูลบุคคล
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {attr.image === '' ? null : (
              <MDBox component='img' src={attr.image} alt={attr.image} width='100%' height='100%' />
            )}
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รหัสพนักงาน *'
              onChange={(e) => setAttr({ ...attr, code: e.target.value })}
              value={attr.code}
              variant='standard'
              inputProps={{
                maxLength: 10,
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='คำนำหน้า'
              labelDefault={t(`element.please_select`)}
              data={cboPrefix}
              value={attr.prefix_id}
              onChange={(e) => setAttr({ ...attr, prefix_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อ *'
              onChange={(e) => setAttr({ ...attr, name: e.target.value })}
              value={attr.name}
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='นามสกุล'
              onChange={(e) => setAttr({ ...attr, surname: e.target.value })}
              value={attr.surname}
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormData = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          ข้อมูลพนักงาน
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDSelect
              label='สาขาหลัก *'
              labelDefault={t(`element.please_select`)}
              data={cboBranch}
              value={attr.branch_id}
              onChange={(e) => setAttr({ ...attr, branch_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelectAuto
              label='สาขาเข้าใช้งาน'
              multiple
              disableCloseOnSelect
              data={cboBranch}
              value={branch}
              onChange={(e, v) => setBranch(v)}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='สิทธิ์การใช้'
              labelDefault={t(`element.please_select`)}
              data={cboRole}
              value={attr.role_id}
              onChange={(e) => setAttr({ ...attr, role_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='แผนก'
              labelDefault={t(`element.please_select`)}
              data={cboDepartment}
              value={attr.department_id}
              onChange={(e) => setAttr({ ...attr, department_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='ตำแหน่ง'
              labelDefault={t(`element.please_select`)}
              data={cboPosition}
              value={attr.position_id}
              onChange={(e) => setAttr({ ...attr, position_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.idcard`)}`}
              onChange={(e) => setAttr({ ...attr, idcard: Func.maskIdCard(e.target.value) })}
              value={attr.idcard}
              variant='standard'
              fullWidth
              type='search'
              inputProps={{
                autoComplete: 'off',
                maxLength: 17,
                placeholder: '0-0000-00000-00-0',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.tel`)}`}
              onChange={(e) => setAttr({ ...attr, tel: Func.maskTel(e.target.value) })}
              value={attr.tel}
              variant='standard'
              fullWidth
              type='search'
              inputProps={{
                autoComplete: 'off',
                maxLength: 14,
                placeholder: '(000) 000 0000',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.email`)}`}
              onChange={(e) => setAttr({ ...attr, email: e.target.value })}
              value={attr.email}
              type='email'
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormCheck = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          สถานะ
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDCheckbox
              label='เข้าสู่ระบบหลังบ้าน'
              checked={attr.enable === 'Y'}
              onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label='เข้าสู่ระบบ App สต็อก'
              checked={attr.login_appstock === 'Y'}
              onChange={(e) => setAttr({ ...attr, login_appstock: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label='เข้าสู่ระบบ App POS'
              checked={attr.login_apppos === 'Y'}
              onChange={(e) => setAttr({ ...attr, login_apppos: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label='เป็นพนักงานขับรถ'
              checked={attr.driver === 'Y'}
              onChange={(e) => setAttr({ ...attr, driver: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label='แสดงข้อมูลหลังบ้านทุกสาขา'
              checked={attr.branch_all === 'Y'}
              onChange={(e) => setAttr({ ...attr, branch_all: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormAddress = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          ที่อยู่
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.address`)}`}
              onChange={(e) => setAttr({ ...attr, address: e.target.value })}
              value={attr.address}
              variant='standard'
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='จังหวัด'
              labelDefault={t(`element.please_select`)}
              data={cboProvince}
              value={attr.province_id}
              onChange={(e) => {
                setAttr({ ...attr, province_id: e.target.value, zipcode: '' });
                district(e.target.value);
              }}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='อำเภอ / เขต'
              labelDefault={t(`element.please_select`)}
              data={cboDistrict}
              value={attr.district_id}
              onChange={(e) => {
                setAttr({ ...attr, district_id: e.target.value, zipcode: '' });
                subDistrict(e.target.value);
              }}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='ตำบล / แขวง'
              labelDefault={t(`element.please_select`)}
              data={cboSubDistrict}
              value={attr.subdistrict_id}
              onChange={(e) => {
                cboSubDistrict.forEach((item, i) => {
                  if (item.id === e.target.value) {
                    setAttr({ ...attr, subdistrict_id: e.target.value, zipcode: item.zip_code });
                  }
                });
              }}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รหัสไปรษณีย์'
              onChange={(e) => setAttr({ ...attr, zipcode: e.target.value })}
              value={attr.zipcode}
              type='number'
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormLine = () => {
    return (
      <MDBox className='card' p={2}>
        <MDTypography variant='h5' mb={3}>
          LINE OA
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {attr.line_pic === '' ? null : (
              <MDBox
                component='img'
                src={attr.line_pic}
                alt={attr.line_pic}
                width='100%'
                height='100%'
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อ LINE'
              value={attr.line_name}
              variant='standard'
              fullWidth
              disabled
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='สถานะ LINE'
              value={attr.line_status}
              variant='standard'
              multiline
              fullWidth
              disabled
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='LINE CODE'
              value={attr.line_code}
              variant='standard'
              multiline
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <SearchCustomer callback={selectCustomer} />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        <MDButton variant='gradient' color='primary' onClick={signIn}>
          <Icon>login</Icon>&nbsp;{t(`authen.signin`)}
        </MDButton>
        <MDButton variant='gradient' color='success' onClick={edit}>
          <Icon>save</Icon>&nbsp;{t(`element.save`)}
        </MDButton>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='tabs-addedit'>
                    <Tab label={t(`element.info`)} />
                    <Tab label='การเข้าใช้ระบบ' />
                    <Tab label={t(`attr.image`)} />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} xl={6}>
                      <MDTypography variant='h3'>
                        {attr.name} {attr.surname} : {attr.office_name}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} xl={6}>
                      {renderButton()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                      {renderFormInfo()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                      {renderFormData()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                      {renderFormAddress()}
                      {renderFormCheck()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                      {renderFormLine()}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
                      {renderAuthen()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
                      &nbsp;
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
                      {renderImage()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
                      {renderSignature()}
                    </Grid>
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.user`), t(`menu.user`)]} />
      {renderPage()}
      {renderAdd()}
      {renderEdit()}
      {renderDelete()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default User;
